import React, { useState, useRef, useEffect } from 'react';
import { useDebugLogs, DebugLogEntry, PortStats } from '../hooks/useDebugLogs';

interface StatsCardProps {
  title: string;
  stats: PortStats;
}

function StatsCard({ title, stats }: StatsCardProps) {
  return (
    <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
      <h4 className="text-xs font-semibold text-slate-400 uppercase mb-2">{title}</h4>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-slate-500">Requests</div>
          <div className="text-slate-200 font-mono">{stats.totalRequests}</div>
        </div>
        <div>
          <div className="text-slate-500">Success</div>
          <div className="text-green-400 font-mono">{stats.successfulRequests}</div>
        </div>
        <div>
          <div className="text-slate-500">Failed</div>
          <div className="text-red-400 font-mono">{stats.failedRequests}</div>
        </div>
        <div>
          <div className="text-slate-500">Avg Time</div>
          <div className="text-cyan-400 font-mono">{stats.avgResponseTime}ms</div>
        </div>
        <div>
          <div className="text-slate-500">Last Time</div>
          <div className="text-slate-200 font-mono">{stats.lastResponseTime}ms</div>
        </div>
        <div>
          <div className="text-slate-500">Packet Loss</div>
          <div className={`font-mono ${stats.packetLossPercent > 5 ? 'text-red-400' : stats.packetLossPercent > 1 ? 'text-yellow-400' : 'text-green-400'}`}>
            {stats.packetLossPercent}%
          </div>
        </div>
      </div>
    </div>
  );
}

interface LogEntryProps {
  entry: DebugLogEntry;
  showPort?: boolean;
}

function LogEntry({ entry, showPort }: LogEntryProps) {
  const time = new Date(entry.timestamp).toLocaleTimeString('en-GB', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  const typeColors: Record<string, string> = {
    read: 'text-cyan-400',
    write: 'text-purple-400',
    control: 'text-yellow-400',
    error: 'text-red-400',
    info: 'text-slate-400'
  };

  const typeLabels: Record<string, string> = {
    read: 'READ',
    write: 'WRITE',
    control: 'CTRL',
    error: 'ERR',
    info: 'INFO'
  };

  return (
    <div className={`font-mono text-xs py-0.5 ${entry.success ? '' : 'bg-red-900/20'}`}>
      <span className="text-slate-500">[{time}]</span>
      {showPort && entry.portId && (
        <span className="text-slate-400 ml-1">[{entry.portId}]</span>
      )}
      <span className={`ml-1 ${typeColors[entry.type] || 'text-slate-400'}`}>
        {typeLabels[entry.type] || entry.type.toUpperCase()}
      </span>
      <span className="text-slate-300 ml-1">{entry.message}</span>
      {entry.responseTime !== undefined && (
        <span className={`ml-1 ${entry.responseTime > 500 ? 'text-yellow-400' : entry.responseTime > 200 ? 'text-cyan-400' : 'text-green-400'}`}>
          ({entry.responseTime}ms)
        </span>
      )}
    </div>
  );
}

export function ComsConsolePanel() {
  const { data, loading, error, refresh } = useDebugLogs(true, 2000);
  const [selectedPort, setSelectedPort] = useState<string | 'all'>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const [userScrolledAway, setUserScrolledAway] = useState(false);

  useEffect(() => {
    if (autoScroll && !userScrolledAway && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [data, autoScroll, userScrolledAway]);

  const handleScroll = () => {
    if (!logContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setUserScrolledAway(!isAtBottom);
  };

  useEffect(() => {
    if (autoScroll) {
      setUserScrolledAway(false);
    }
  }, [autoScroll]);

  const ports = data ? Object.keys(data.ports) : [];
  const currentLog = selectedPort === 'all'
    ? (data?.aggregate.log || [])
    : (data?.ports[selectedPort]?.log || []);
  const currentStats = selectedPort === 'all'
    ? data?.aggregate.stats
    : data?.ports[selectedPort]?.stats;

  return (
    <div className="space-y-4">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-slate-200">Modbus Communications</h2>
          <button
            onClick={refresh}
            className="text-xs px-2 py-1 bg-slate-800 text-slate-400 rounded hover:bg-slate-700 border border-slate-700"
          >
            Refresh
          </button>
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              className="rounded bg-slate-700 border-slate-600"
            />
            Auto-scroll
          </label>
        </div>
      </div>

      {/* Port Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setSelectedPort('all')}
          className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
            selectedPort === 'all'
              ? 'bg-cyan-600 text-white'
              : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'
          }`}
        >
          All Ports
        </button>
        {ports.map(portId => (
          <button
            key={portId}
            onClick={() => setSelectedPort(portId)}
            className={`px-3 py-1.5 text-xs rounded-lg whitespace-nowrap transition-colors ${
              selectedPort === portId
                ? 'bg-cyan-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'
            }`}
          >
            {portId}
            <span className="ml-1 text-slate-500">
              ({data?.ports[portId]?.deviceType || '?'})
            </span>
          </button>
        ))}
      </div>

      {/* Stats */}
      <div>
        {loading && !data ? (
          <div className="text-slate-500 text-sm">Loading stats...</div>
        ) : error ? (
          <div className="text-red-400 text-sm">{error}</div>
        ) : currentStats ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedPort === 'all' ? (
              <>
                <StatsCard title="Aggregate Stats" stats={currentStats} />
                {ports.slice(0, 5).map(portId => (
                  <StatsCard
                    key={portId}
                    title={`${portId} (${data?.ports[portId]?.deviceType})`}
                    stats={data?.ports[portId]?.stats!}
                  />
                ))}
              </>
            ) : (
              <StatsCard title={`${selectedPort} Stats`} stats={currentStats} />
            )}
          </div>
        ) : (
          <div className="text-slate-500 text-sm">No stats available</div>
        )}
      </div>

      {/* Log Output */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg">
        <div className="flex items-center justify-between p-3 border-b border-slate-700">
          <h3 className="text-sm font-semibold text-slate-300">Communication Log</h3>
          <span className="text-xs text-slate-500">{currentLog.length} entries</span>
        </div>
        <div
          ref={logContainerRef}
          onScroll={handleScroll}
          className="overflow-auto p-2 bg-slate-950"
          style={{ minHeight: '300px', maxHeight: '500px' }}
        >
          {currentLog.length === 0 ? (
            <div className="text-slate-500 text-sm p-4 text-center">
              No log entries yet. Waiting for Modbus activity...
            </div>
          ) : (
            <div className="space-y-0">
              {[...currentLog].reverse().map((entry, idx) => (
                <LogEntry
                  key={`${entry.timestamp}-${idx}`}
                  entry={entry}
                  showPort={selectedPort === 'all'}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer Info */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>Showing {currentLog.length} entries</span>
        <span>
          {data && selectedPort === 'all' && (
            <>Active ports: {ports.length}</>
          )}
        </span>
      </div>
    </div>
  );
}
